import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  X, ArrowRight, ArrowLeft, Check, Plus, Clock, Users,
  Camera, Coffee, Send, Rocket, Mail, Trash2,
  Monitor, Shield, BarChart3, ChevronLeft, ChevronRight, Play, Pause,
  Zap, Globe, CalendarDays, Eye, Timer, MousePointerClick,
  Download, MapPin, Activity, Wifi, WifiOff, PanelTop
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

const HOUR_OPTIONS = [6, 7, 8, 9, 10];
const SCREENSHOT_OPTIONS = [5, 10, 15];
const IDLE_OPTIONS = [5, 10, 15];

const TIMEZONES = [
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Riyadh',
  'US/Eastern', 'US/Pacific', 'Europe/London', 'UTC',
];

/* ─── Animated Tour Slide ─── */
const TourSlide = ({ active, children, delay = 0 }) => (
  <div
    className="absolute inset-0 flex flex-col items-center justify-center p-8 transition-all duration-700"
    style={{
      opacity: active ? 1 : 0,
      transform: active ? 'translateX(0) scale(1)' : 'translateX(60px) scale(0.95)',
      pointerEvents: active ? 'auto' : 'none',
      transitionDelay: active ? `${delay}ms` : '0ms',
    }}
  >
    {children}
  </div>
);

/* ─── Animated Counter ─── */
const AnimCounter = ({ target, suffix = '', duration = 1200 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) clearInterval(ref.current);
    const steps = 30;
    const inc = target / steps;
    let curr = 0;
    ref.current = setInterval(() => {
      curr += inc;
      if (curr >= target) { setVal(target); clearInterval(ref.current); }
      else setVal(Math.round(curr));
    }, duration / steps);
    return () => clearInterval(ref.current);
  }, [target, duration]);
  return <>{val}{suffix}</>;
};

/* ─── Typing Text ─── */
const TypeWriter = ({ text, speed = 40 }) => {
  const [displayed, setDisplayed] = useState('');
  useEffect(() => {
    setDisplayed('');
    let i = 0;
    const interval = setInterval(() => {
      if (i < text.length) { setDisplayed(text.slice(0, i + 1)); i++; }
      else clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);
  return <>{displayed}<span className="animate-pulse">|</span></>;
};

/* ─── Pulsing Ring ─── */
const PulseRing = ({ color = 'blue', size = 80, children }) => (
  <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
    <div className="absolute inset-0 rounded-full animate-ping opacity-20" style={{ background: `var(--color-${color}-400, #60a5fa)` }} />
    <div className="absolute inset-1 rounded-full animate-pulse opacity-10" style={{ background: `var(--color-${color}-300, #93c5fd)` }} />
    <div className="relative z-10">{children}</div>
  </div>
);

export default function SetupWizard({ onComplete, settings, isFreePlan = false }) {
  const { token, user } = useAuth();
  const [step, setStep] = useState(isFreePlan ? 3 : 0);
  const [subStep, setSubStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [fadeClass, setFadeClass] = useState('opacity-100');

  // Step 1
  const [workingDays, setWorkingDays] = useState(
    settings?.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
  );
  const [dailyHours, setDailyHours] = useState(settings?.daily_hours_target || 8);
  const [timezone, setTimezone] = useState(settings?.default_timezone || 'Asia/Kolkata');
  const [screenshotFreq, setScreenshotFreq] = useState(settings?.screen_capture_frequency || 10);
  const [idleThreshold, setIdleThreshold] = useState(settings?.idle_threshold || 10);

  // Step 2
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteEmails, setInviteEmails] = useState([]);
  const [sendingInvites, setSendingInvites] = useState(false);

  // Tour
  const [tourSlide, setTourSlide] = useState(0);
  const [tourPlaying, setTourPlaying] = useState(false);
  const tourTimer = useRef(null);

  const animateTransition = (cb) => {
    setFadeClass('opacity-0 translate-y-3');
    setTimeout(() => { cb(); setFadeClass('opacity-100 translate-y-0'); }, 250);
  };

  const toggleDay = (day) => {
    setWorkingDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const addEmail = () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email'); return;
    }
    if (inviteEmails.includes(email)) { toast.error('Already added'); return; }
    setInviteEmails(prev => [...prev, email]);
    setInviteEmail('');
  };

  const removeEmail = (email) => setInviteEmails(prev => prev.filter(e => e !== email));

  const saveSettings = useCallback(async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/admin/workspace-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          working_days: workingDays, daily_hours_target: dailyHours,
          default_timezone: timezone, screen_capture_frequency: screenshotFreq,
          idle_threshold: idleThreshold,
        }),
      });
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  }, [token, workingDays, dailyHours, timezone, screenshotFreq, idleThreshold]);

  const sendInvites = async () => {
    if (!inviteEmails.length) return;
    setSendingInvites(true);
    let ok = 0;
    for (const email of inviteEmails) {
      try {
        const res = await fetch(`${API_URL}/api/admin/people/invitations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ email, role: 'employee' }),
        });
        if (res.ok) ok++;
        else { const d = await res.json(); toast.error(`${email}: ${d.detail}`); }
      } catch { toast.error(`Failed: ${email}`); }
    }
    if (ok) toast.success(`${ok} invite(s) sent!`);
    setSendingInvites(false); setInviteEmails([]);
  };

  const completeSetup = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/admin/workspace-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ setup_completed: true }),
      });
    } catch (err) { console.error(err); }
    finally { setSaving(false); onComplete(); }
  };

  // ── Tour slides data (personalized) ──
  const tourSlides = [
    {
      icon: <CalendarDays className="w-8 h-8 text-blue-500" />,
      color: 'from-blue-500/10 to-indigo-500/5',
      border: 'border-blue-200',
      title: 'Your Work Schedule',
      description: `Your team works ${workingDays.length} days a week with a ${dailyHours}h daily target.`,
      visual: (
        <div className="flex gap-2 mt-4">
          {DAYS.map(d => (
            <div key={d.key} className={`w-11 h-11 rounded-lg flex items-center justify-center text-xs font-bold transition-all duration-500 ${
              workingDays.includes(d.key)
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-300/40 scale-110'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-300 scale-90'
            }`}>{d.label}</div>
          ))}
        </div>
      ),
      stat: { value: dailyHours * workingDays.length, label: 'hrs/week', icon: <Clock className="w-4 h-4" /> },
    },
    {
      icon: <Camera className="w-8 h-8 text-violet-500" />,
      color: 'from-violet-500/10 to-purple-500/5',
      border: 'border-violet-200',
      title: 'Smart Monitoring',
      description: `Screenshots every ${screenshotFreq} min. Idle detection after ${idleThreshold} min of inactivity.`,
      visual: (
        <div className="flex items-center gap-6 mt-4">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mb-2 relative">
              <Monitor className="w-7 h-7 text-violet-600" />
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-violet-50 dark:bg-violet-900/30 rounded-full flex items-center justify-center">
                <Camera className="w-3 h-3 text-white" />
              </div>
            </div>
            <span className="text-xs text-violet-600 font-semibold">Every {screenshotFreq}m</span>
          </div>
          <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-violet-500 to-purple-400 rounded-full animate-pulse" style={{ width: '65%' }} />
          </div>
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mb-2">
              <Coffee className="w-7 h-7 text-amber-600" />
            </div>
            <span className="text-xs text-amber-600 font-semibold">Idle: {idleThreshold}m</span>
          </div>
        </div>
      ),
      stat: { value: Math.round((dailyHours * 60) / screenshotFreq), label: 'captures/day', icon: <Eye className="w-4 h-4" /> },
    },
    {
      icon: <Download className="w-8 h-8 text-indigo-500" />,
      color: 'from-indigo-500/10 to-blue-500/5',
      border: 'border-indigo-200',
      title: 'Desktop Tracking Agent',
      description: 'A standalone native app for Windows & Mac with system tray, mini floating timer, and offline mode.',
      visual: (
        <div className="flex items-center gap-4 mt-4">
          {[
            { icon: <PanelTop className="w-5 h-5 text-indigo-500" />, label: 'Mini Widget', sub: 'Always on top' },
            { icon: <Wifi className="w-5 h-5 text-emerald-500" />, label: 'Offline Mode', sub: 'Auto-syncs' },
            { icon: <Shield className="w-5 h-5 text-blue-500" />, label: 'Admin Controls', sub: 'Hybrid settings' },
          ].map((f, i) => (
            <div key={i} className="flex-1 bg-white dark:bg-slate-800 rounded-xl p-3 text-center border border-slate-100 dark:border-slate-700 shadow-sm">
              <div className="flex justify-center mb-2">{f.icon}</div>
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">{f.label}</p>
              <p className="text-[10px] text-slate-400">{f.sub}</p>
            </div>
          ))}
        </div>
      ),
      stat: { value: 2, label: 'platforms', icon: <Monitor className="w-4 h-4" /> },
    },
    {
      icon: <Activity className="w-8 h-8 text-rose-500" />,
      color: 'from-rose-500/10 to-pink-500/5',
      border: 'border-rose-200',
      title: 'Mouse & Keyboard Activity',
      description: 'Hubstaff-style activity tracking shows real engagement — mouse movements, clicks, and keyboard usage.',
      visual: (
        <div className="mt-4">
          <div className="flex items-center gap-3 mb-3">
            {[
              { label: 'Activity', pct: 72, color: 'bg-rose-500' },
              { label: 'Mouse', pct: 58, color: 'bg-pink-400' },
              { label: 'Keyboard', pct: 34, color: 'bg-fuchsia-400' },
            ].map((bar, i) => (
              <div key={i} className="flex-1">
                <div className="flex justify-between mb-1">
                  <span className="text-[10px] font-semibold text-slate-500">{bar.label}</span>
                  <span className="text-[10px] font-bold text-slate-700">{bar.pct}%</span>
                </div>
                <div className="h-2.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div className={`h-full ${bar.color} rounded-full transition-all duration-1000`} style={{ width: `${bar.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
      stat: { value: 72, label: '% avg activity', icon: <MousePointerClick className="w-4 h-4" /> },
    },
    {
      icon: <MapPin className="w-8 h-8 text-teal-500" />,
      color: 'from-teal-500/10 to-emerald-500/5',
      border: 'border-teal-200',
      title: 'Team Locations & GPS',
      description: 'Track employee locations in real-time with interactive maps, trails, and checkpoint history.',
      visual: (
        <div className="mt-4 relative bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-4 border border-teal-100">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {['#4f46e5', '#06b6d4', '#8b5cf6'].map((c, i) => (
                <div key={i} className="w-9 h-9 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold text-white shadow-sm"
                  style={{ background: c }}>{['AR', 'VS', 'IA'][i]}</div>
              ))}
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-teal-700">3 team members tracked</p>
              <p className="text-[10px] text-teal-500">Live locations on interactive map</p>
            </div>
            <MapPin className="w-6 h-6 text-teal-400 animate-bounce" />
          </div>
          <div className="flex gap-2 mt-3">
            {['Start', 'Checkpoint', 'Stop'].map((t, i) => (
              <span key={t} className={`flex-1 text-center text-[9px] font-semibold py-1 rounded-md ${
                i === 0 ? 'bg-green-100 text-green-700' : i === 1 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
              }`}>{t}</span>
            ))}
          </div>
        </div>
      ),
      stat: { value: 24, label: '/7 tracking', icon: <Globe className="w-4 h-4" /> },
    },
    {
      icon: <Globe className="w-8 h-8 text-sky-500" />,
      color: 'from-sky-500/10 to-cyan-500/5',
      border: 'border-sky-200',
      title: 'App & Website Monitoring',
      description: 'See which apps and websites your team uses. Timeline view shows the exact sequence with timestamps.',
      visual: (
        <div className="mt-4 space-y-2">
          {[
            { name: 'VS Code', cat: 'Productive', time: '2h 15m', color: 'bg-emerald-100 text-emerald-700' },
            { name: 'outlook.com', cat: 'Productive', time: '45m', color: 'bg-emerald-100 text-emerald-700' },
            { name: 'chatgpt.com', cat: 'Neutral', time: '20m', color: 'bg-slate-100 text-slate-600' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-lg p-2.5 border border-slate-100 dark:border-slate-700">
              <div className="w-7 h-7 rounded-md bg-sky-100 flex items-center justify-center">
                {i === 0 ? <Monitor className="w-3.5 h-3.5 text-sky-600" /> : <Globe className="w-3.5 h-3.5 text-sky-600" />}
              </div>
              <span className="text-xs font-medium text-slate-700 dark:text-slate-300 flex-1">{item.name}</span>
              <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${item.color}`}>{item.cat}</span>
              <span className="text-xs font-bold text-slate-500">{item.time}</span>
            </div>
          ))}
        </div>
      ),
      stat: { value: 95, label: '% productive', icon: <BarChart3 className="w-4 h-4" /> },
    },
    {
      icon: <Users className="w-8 h-8 text-emerald-500" />,
      color: 'from-emerald-500/10 to-teal-500/5',
      border: 'border-emerald-200',
      title: isFreePlan ? 'Your Workspace' : 'Team Ready',
      description: isFreePlan
        ? 'Your personal workspace is set up and ready to go!'
        : inviteEmails.length > 0
          ? `${inviteEmails.length} teammate(s) invited! They'll get a magic link to join.`
          : 'Invite your team anytime from the People page.',
      visual: (
        <div className="flex items-center justify-center gap-3 mt-4">
          {[user?.name?.charAt(0) || 'A', ...inviteEmails.slice(0, 3).map(e => e.charAt(0).toUpperCase())].map((c, i) => (
            <div
              key={i}
              className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold border-2 border-white shadow-md transition-all duration-500"
              style={{
                background: ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b'][i % 4],
                color: 'white',
                animationDelay: `${i * 150}ms`,
                transform: `translateY(${i % 2 === 0 ? '-4px' : '4px'})`,
              }}
            >{c}</div>
          ))}
          {inviteEmails.length > 3 && (
            <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-500">
              +{inviteEmails.length - 3}
            </div>
          )}
        </div>
      ),
      stat: { value: (inviteEmails.length || 0) + 1, label: 'team members', icon: <Users className="w-4 h-4" /> },
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-cyan-500" />,
      color: 'from-cyan-500/10 to-blue-500/5',
      border: 'border-cyan-200',
      title: 'AI-Powered Insights',
      description: 'Get daily briefings, productivity trends, and smart recommendations as your team tracks time.',
      visual: (
        <div className="flex items-end gap-2 mt-4 h-20">
          {[40, 65, 50, 80, 70, 90, 75].map((h, i) => (
            <div key={i} className="flex-1 rounded-t-md transition-all duration-700 bg-gradient-to-t from-cyan-500 to-blue-400"
              style={{ height: `${h}%`, animationDelay: `${i * 100}ms`, opacity: 0.6 + (i * 0.05) }} />
          ))}
        </div>
      ),
      stat: { value: 99.9, label: '% uptime', icon: <Zap className="w-4 h-4" /> },
    },
  ];

  // Auto-play tour
  useEffect(() => {
    if (step === 3 && tourPlaying) {
      tourTimer.current = setInterval(() => {
        setTourSlide(prev => {
          if (prev >= tourSlides.length - 1) { setTourPlaying(false); return prev; }
          return prev + 1;
        });
      }, 4000);
    }
    return () => clearInterval(tourTimer.current);
  }, [step, tourPlaying, tourSlides.length]);

  // Start tour auto-play when entering step 3
  useEffect(() => {
    if (step === 3) {
      setTourSlide(0);
      setTimeout(() => setTourPlaying(true), 800);
    }
  }, [step]);

  const handleStep1Continue = async () => {
    if (subStep === 0) animateTransition(() => setSubStep(1));
    else { await saveSettings(); animateTransition(() => setStep(2)); }
  };

  const handleStep1Back = () => {
    if (subStep === 1) animateTransition(() => setSubStep(0));
    else animateTransition(() => setStep(0));
  };

  /* ─── Option Card ─── */
  const OptionCard = ({ selected, onClick, label, recommended, testId }) => (
    <button onClick={onClick} data-testid={testId}
      className={`relative flex-1 py-4 px-5 rounded-xl border-2 text-center font-semibold transition-all duration-300 ${
        selected
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 shadow-md shadow-blue-100 scale-[1.03]'
          : 'border-slate-200 bg-white dark:bg-slate-800 text-slate-500 hover:border-slate-300 hover:shadow-sm'
      }`}
    >
      {recommended && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-400 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
          Recommended
        </span>
      )}
      {label}
    </button>
  );

  /* ─── Step Indicator ─── */
  const StepIndicator = ({ current }) => {
    const steps = [
      { num: 1, label: 'Configure' },
      { num: 2, label: 'Invite Team' },
      { num: 3, label: 'Tour & Go' },
    ];
    return (
      <div className="flex items-center justify-center gap-0 mb-8" data-testid="setup-step-indicator">
        {steps.map((s, i) => (
          <React.Fragment key={s.num}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500 ${
                current > s.num ? 'bg-emerald-50 dark:bg-emerald-900/30 text-white scale-100' :
                current === s.num ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-110' :
                'bg-slate-100 dark:bg-slate-700 text-slate-400 scale-95'
              }`}>
                {current > s.num ? <Check className="w-5 h-5" /> : s.num}
              </div>
              <span className={`text-[11px] mt-1.5 font-medium transition-colors ${
                current >= s.num ? 'text-slate-700' : 'text-slate-400'
              }`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className="w-20 h-0.5 mx-2 mb-5 bg-slate-200 rounded-full overflow-hidden">
                <div className={`h-full bg-emerald-50 dark:bg-emerald-900/30 rounded-full transition-all duration-700 ${
                  current > s.num ? 'w-full' : 'w-0'
                }`} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="setup-wizard">
      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-[1000px] max-h-[92vh] overflow-hidden flex" data-testid="setup-wizard-modal">

        {/* ── Left Panel ── */}
        <div className="hidden md:flex w-[360px] bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 flex-col items-center justify-center p-8 relative overflow-hidden flex-shrink-0">
          {/* Animated grid */}
          <div className="absolute inset-0 opacity-[0.06]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }} />
          {/* Glows */}
          <div className="absolute top-20 left-1/2 -translate-x-1/2 w-60 h-60 bg-blue-50 dark:bg-blue-900/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-0 w-40 h-40 bg-indigo-50 dark:bg-indigo-900/15 rounded-full blur-2xl" />

          <div className="relative z-10 flex flex-col items-center">
            <div className="w-14 h-14 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mb-5 border border-white/10 overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_4bb7599f-12e1-4642-86e6-3a655f966be6/artifacts/jx2km18t_image.png" alt="InspiroTrack" className="w-12 h-12 rounded-xl" />
            </div>
            <h2 className="text-2xl font-bold text-white text-center mb-1">Inspiro Tracker</h2>
            <p className="text-blue-300/60 text-sm text-center mb-8">Time Management</p>
            <div className="w-full rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-black/30">
              <img src="/app-screenshot.png" alt="Inspiro Tracker" className="w-full" onError={e => { e.target.style.display = 'none'; }} />
            </div>
            {/* Feature pills */}
            <div className="flex flex-wrap gap-2 mt-6 justify-center">
              {['Desktop Agent', 'Activity %', 'GPS Tracking', 'AI Insights', 'Screenshots'].map(f => (
                <span key={f} className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-blue-200 text-[10px] font-medium">{f}</span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right Panel ── */}
        <div className="flex-1 flex flex-col min-h-0 relative">
          <button onClick={completeSetup} data-testid="setup-close-btn"
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:bg-slate-700 transition-all z-20"
          ><X className="w-5 h-5" /></button>

          <div className={`flex-1 overflow-y-auto p-8 transition-all duration-300 ${fadeClass}`}>

            {/* ════ WELCOME ════ */}
            {step === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center" data-testid="setup-welcome">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mb-6">
                  <Zap className="w-8 h-8 text-blue-600" />
                </div>
                <h1 className="text-3xl font-extrabold text-slate-900 mb-2 leading-tight">
                  Set up Inspiro Tracker<br />for your team
                </h1>
                <p className="text-slate-500 mb-10">Takes about <strong className="text-slate-700">2 minutes</strong>. Ends with a personalized tour.</p>

                <div className="w-full max-w-sm space-y-3 mb-10">
                  {[
                    { num: 1, text: 'Configure workspace rules', icon: <Monitor className="w-4 h-4" /> },
                    { num: 2, text: 'Invite your team', icon: <Users className="w-4 h-4" /> },
                    { num: 3, text: 'Personalized feature tour', icon: <Play className="w-4 h-4" /> },
                  ].map((item, i) => (
                    <div key={item.num}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100 dark:border-slate-700 transition-all duration-300 hover:shadow-sm hover:border-slate-200"
                      style={{ animationDelay: `${i * 100}ms` }}
                    >
                      <span className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {item.num}
                      </span>
                      <span className="text-slate-700 font-medium text-left flex-1">{item.text}</span>
                      <span className="text-slate-300">{item.icon}</span>
                    </div>
                  ))}
                </div>

                <Button onClick={() => animateTransition(() => setStep(1))} size="lg"
                  className="w-full max-w-sm h-12 text-base font-bold bg-blue-600 hover:bg-blue-700 group"
                  data-testid="start-setup-btn"
                >
                  Start Setup <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            )}

            {/* ════ STEP 1: CONFIGURE ════ */}
            {step === 1 && (
              <div data-testid="setup-step-1">
                <StepIndicator current={1} />

                {subStep === 0 && (
                  <div className="space-y-7">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">Working Days</h3>
                      <p className="text-sm text-slate-500 mb-3">Select which days your team works</p>
                      <div className="flex gap-2">
                        {DAYS.map(d => (
                          <button key={d.key} onClick={() => toggleDay(d.key)} data-testid={`day-${d.key}`}
                            className={`flex-1 py-3 rounded-xl text-sm font-semibold border-2 transition-all duration-200 ${
                              workingDays.includes(d.key)
                                ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-200/50'
                                : 'bg-white dark:bg-slate-800 text-slate-500 border-slate-200 hover:border-slate-300'
                            }`}
                          >{d.label}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">Daily Hours Target</h3>
                      <p className="text-sm text-slate-500 mb-3">Expected working hours per day</p>
                      <div className="flex gap-3">
                        {HOUR_OPTIONS.map(h => (
                          <OptionCard key={h} selected={dailyHours === h} onClick={() => setDailyHours(h)}
                            label={`${h}h`} recommended={h === 8} testId={`hours-${h}`} />
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">Default Timezone</h3>
                      <p className="text-sm text-slate-500 mb-3">Your team's primary timezone</p>
                      <select value={timezone} onChange={e => setTimezone(e.target.value)} data-testid="timezone-select"
                        className="w-full h-11 px-4 border-2 border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:border-blue-500 focus:ring-0 bg-white dark:bg-slate-800"
                      >
                        {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                {subStep === 1 && (
                  <div className="space-y-8">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <Camera className="w-5 h-5 text-violet-600" />
                        <h3 className="text-lg font-bold text-slate-900">Screenshot Frequency</h3>
                      </div>
                      <p className="text-sm text-slate-500 mb-4">How often should screenshots be captured during tracking?</p>
                      <div className="flex gap-3">
                        {SCREENSHOT_OPTIONS.map(m => (
                          <OptionCard key={m} selected={screenshotFreq === m} onClick={() => setScreenshotFreq(m)}
                            label={`${m} mins`} recommended={m === 5} testId={`screenshot-${m}`} />
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <Coffee className="w-5 h-5 text-amber-600" />
                        <h3 className="text-lg font-bold text-slate-900">Idle Time Limit</h3>
                      </div>
                      <p className="text-sm text-slate-500 mb-4">How long with no activity counts as idle time?</p>
                      <div className="flex gap-3">
                        {IDLE_OPTIONS.map(m => (
                          <OptionCard key={m} selected={idleThreshold === m} onClick={() => setIdleThreshold(m)}
                            label={`${m} mins`} recommended={m === 5} testId={`idle-${m}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-100 dark:border-slate-700">
                  <Button variant="ghost" onClick={handleStep1Back} data-testid="step1-back-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <Button onClick={handleStep1Continue} disabled={saving}
                    className="h-11 px-8 bg-blue-600 hover:bg-blue-700 font-bold group"
                    data-testid="step1-continue-btn"
                  >
                    {saving ? 'Saving...' : 'Continue'} <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            )}

            {/* ════ STEP 2: INVITE ════ */}
            {step === 2 && (
              <div data-testid="setup-step-2">
                <StepIndicator current={2} />
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-1">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-bold text-slate-900">Invite via Email</h3>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">Type one or more email addresses</p>
                  <div className="flex gap-2">
                    <Input type="email" value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEmail())}
                      placeholder="alex@company.com" className="h-11 flex-1" data-testid="invite-email-input" />
                    <Button onClick={addEmail} className="h-11 px-6 bg-blue-600 hover:bg-blue-700" data-testid="invite-add-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>

                {inviteEmails.length > 0 ? (
                  <div className="space-y-2 mb-6" data-testid="invite-email-list">
                    {inviteEmails.map(email => (
                      <div key={email} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 dark:border-slate-700 group">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                            {email.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm text-slate-700 font-medium">{email}</span>
                        </div>
                        <button onClick={() => removeEmail(email)} data-testid={`remove-email-${email}`}
                          className="text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10">
                    <Users className="w-12 h-12 text-slate-200 mb-3" />
                    <p className="text-slate-400 text-sm">Add emails above to invite your team</p>
                  </div>
                )}

                <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-100 dark:border-slate-700">
                  <Button variant="ghost" onClick={() => animateTransition(() => setStep(1))} data-testid="step2-back-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <div className="flex gap-3">
                    <Button variant="ghost" onClick={() => animateTransition(() => setStep(3))}
                      className="text-slate-500" data-testid="skip-invite-btn">
                      Skip For Now
                    </Button>
                    {inviteEmails.length > 0 && (
                      <Button onClick={async () => { await sendInvites(); animateTransition(() => setStep(3)); }}
                        disabled={sendingInvites} className="h-11 px-6 bg-blue-600 hover:bg-blue-700 font-bold"
                        data-testid="send-invites-btn">
                        {sendingInvites ? 'Sending...' : 'Send Invites'} <Send className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ════ STEP 3: PERSONALIZED TOUR ════ */}
            {step === 3 && (
              <div data-testid="setup-step-3">
                {!isFreePlan && <StepIndicator current={3} />}

                <div className="text-center mb-4">
                  <h2 className="text-2xl font-extrabold text-slate-900">
                    {isFreePlan ? 'Welcome to Inspiro Tracker!' : 'Your Personalized Tour'}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {isFreePlan ? "Here's a quick look at your workspace" : "Here's what we've set up for you"}
                  </p>
                </div>

                {/* Tour Carousel */}
                <div className="relative bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 overflow-hidden" style={{ minHeight: 320 }} data-testid="tour-carousel">
                  {tourSlides.map((slide, i) => (
                    <TourSlide key={i} active={tourSlide === i}>
                      <div className={`w-full max-w-md mx-auto bg-gradient-to-br ${slide.color} rounded-2xl border ${slide.border} p-6`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-800 shadow-sm flex items-center justify-center">
                            {slide.icon}
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-slate-900">{slide.title}</h3>
                            <p className="text-sm text-slate-500">{slide.description}</p>
                          </div>
                        </div>
                        {slide.visual}
                        {slide.stat && (
                          <div className="mt-4 flex items-center justify-center gap-2 text-slate-600">
                            {slide.stat.icon}
                            <span className="text-2xl font-extrabold text-slate-900">
                              <AnimCounter target={slide.stat.value} />
                            </span>
                            <span className="text-sm text-slate-500">{slide.stat.label}</span>
                          </div>
                        )}
                      </div>
                    </TourSlide>
                  ))}
                </div>

                {/* Tour controls */}
                <div className="flex items-center justify-center gap-4 mt-4">
                  <button onClick={() => { setTourPlaying(false); setTourSlide(Math.max(0, tourSlide - 1)); }}
                    className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
                    data-testid="tour-prev">
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Dots */}
                  <div className="flex items-center gap-2">
                    {tourSlides.map((_, i) => (
                      <button key={i} onClick={() => { setTourPlaying(false); setTourSlide(i); }}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          tourSlide === i ? 'w-6 bg-blue-600' : 'w-2 bg-slate-300 hover:bg-slate-400'
                        }`} data-testid={`tour-dot-${i}`} />
                    ))}
                  </div>

                  <button onClick={() => { setTourPlaying(false); setTourSlide(Math.min(tourSlides.length - 1, tourSlide + 1)); }}
                    className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
                    data-testid="tour-next">
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  <button onClick={() => setTourPlaying(!tourPlaying)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      tourPlaying ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 hover:bg-slate-200'
                    }`} data-testid="tour-playpause">
                    {tourPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                </div>

                {/* CTA */}
                <div className="mt-6 flex justify-center">
                  <Button onClick={completeSetup} size="lg" disabled={saving}
                    className="h-12 px-10 text-base font-bold bg-blue-600 hover:bg-blue-700 group"
                    data-testid="go-to-dashboard-btn">
                    {saving ? 'Finishing...' : 'Go to Dashboard'} <Rocket className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
